import type { Messages } from '../index';

export const emails: Messages['emails'] = {
  'member-invite': {
    subject: 'Welcome to {workspaceName} – set up your profile',
    title: 'Welcome to {workspaceName}',
    greeting: 'Hi {firstName},',
    'greeting-fallback': 'Hi,',
    body:
      '{greeting}\n' +
      '\n' +
      '{inviterName} has invited you to {workspaceName}. Set up your profile in a few minutes — photo, position and contact details — so you can get started right away and appear correctly in exposés and on your agent profile.',
    'cta-label': 'Start onboarding',
    footnote:
      'The link is valid for 14 days and is just for you. Not expecting this? You can safely ignore this email.',
  },
  'new-lead': {
    subject: 'New lead: {lastName} ({location})',
    'subject-no-location': 'New lead: {lastName}',
    title: 'New owner lead',
    greeting: 'Hi {firstName},',
    'greeting-fallback': 'Hi,',
    intro:
      'a new owner lead has been assigned to you. You can contact {ownerFirstName} directly — the key details are below.',
    'intro-no-name': 'a new owner lead has been assigned to you. The key details are below.',
    // Heads-up copy for owners / admins: the lead is not assigned to them, so it
    // must never claim it is.
    'intro-cc': 'a new owner lead has come in for your workspace. The key details are below.',
    'group-contact': 'Owner contact',
    'line-name': 'Name: {name}',
    'line-phone': 'Phone: {phone}',
    'line-email': 'Email: {email}',
    'group-object': 'Property',
    'line-address': 'Address: {address}',
    'line-object-type': 'Property type: {type}',
    'eckdaten-both': 'Key facts: {area} m² · built {year}',
    'eckdaten-area': 'Key facts: {area} m²',
    'eckdaten-year': 'Key facts: built {year}',
    'line-source': 'Source: {source}',
    'line-standort': 'Location: {standort}',
    'standort-none': 'no location',
    'group-note': 'Note',
    'cta-label': 'Open lead',
    'fallback-note':
      'This lead could not be matched to a location manager and was assigned to you as the workspace owner.',
    'cc-note': 'You are receiving this email because you are an owner or admin of this workspace.',
    'source-label':
      '{source, select, MANUAL {Manually captured} PARTNER {Partner} PORTAL {Portal} WEBSITE {Website} REFERRAL {Referral} COLD_OUTREACH {Cold outreach} other {Other}}',
  },
  'lead-qualified': {
    subject: 'Lead qualified: {lastName} ({location})',
    'subject-no-location': 'Lead qualified: {lastName}',
    title: 'Lead qualified',
    greeting: 'Hi {firstName},',
    'greeting-fallback': 'Hi,',
    intro:
      '{actorName} qualified an owner lead on {date} at {time}. A contact, a property and a deal were created from it.',
    'intro-fallback':
      'A team member qualified an owner lead on {date} at {time}. A contact, a property and a deal were created from it.',
    'group-contact': 'Owner contact',
    'line-name': 'Name: {name}',
    'line-phone': 'Phone: {phone}',
    'line-email': 'Email: {email}',
    'group-object': 'Property',
    'line-address': 'Address: {address}',
    'line-object-type': 'Property type: {type}',
    'eckdaten-both': 'Key facts: {area} m² · built {year}',
    'eckdaten-area': 'Key facts: {area} m²',
    'eckdaten-year': 'Key facts: built {year}',
    'group-note': 'Note',
    'line-source': 'Source: {source}',
    'line-standort': 'Location: {standort}',
    'line-startphase': 'Start phase: {phase}',
    'cta-label': 'Open deal',
    'source-label':
      '{source, select, MANUAL {Manually captured} PARTNER {Partner} PORTAL {Portal} WEBSITE {Website} REFERRAL {Referral} COLD_OUTREACH {Cold outreach} other {Other}}',
    'startphase-label':
      '{phase, select, IN_EXCHANGE {In exchange} APPOINTMENT_SCHEDULED {Appointment scheduled} other {In exchange}}',
  },
  'valuation-completed': {
    subject: 'Valuation ready: {address}',
    'subject-no-address': 'Valuation ready',
    title: 'Valuation ready',
    greeting: 'Hi {firstName},',
    'greeting-fallback': 'Hi,',
    intro: 'the valuation is ready. Here is the result:',
    'group-result': 'Result',
    'line-price-range': 'Price range: {from} – {to}',
    'line-recommended': 'Recommended start price: {price}',
    'group-object': 'Property',
    'line-address': 'Address: {address}',
    'line-object-type': 'Property type: {type}',
    'line-usage': 'Usage: {usage}',
    'cta-label': 'Open valuation',
    'objectType-label':
      '{type, select, CONDOMINIUM {Condominium} SINGLE_FAMILY_TERRACED_HOUSE {Single-family/terraced house} MULTI_FAMILY_HOUSE {Multi-family house} PLOT {Plot} COMMERCIAL_PROPERTY {Commercial property} GARAGE_PARKING_SPACE {Garage/parking space} other {Property}}',
    'usage-label':
      '{usage, select, OWNER_OCCUPIED {Owner-occupied} RENTED {Rented} PARTIALLY_RENTED {Partially rented} VACANT {Vacant} other {–}}',
  },
  'partner-onboarding-invite': {
    subject: 'Onboarding invitation for "{workspaceName}"',
    title: 'Onboarding for your location',
    greeting: 'Hi {fullName},',
    'greeting-fallback': 'Hi,',
    body:
      '{greeting}\n' +
      '\n' +
      'you have been invited as the location manager for "{workspaceName}". Complete your location onboarding using the button below. The link is valid for 14 days.',
    'cta-label': 'Start onboarding',
    footnote: 'If you weren’t expecting this invitation, you can safely ignore this email.',
  },
  'partner-onboarding-submitted': {
    subject: 'Onboarding submitted: "{organizationName}"',
    title: 'Onboarding submitted for review',
    greeting: 'Hi {firstName},',
    'greeting-fallback': 'Hi,',
    body:
      '{greeting}\n' +
      '\n' +
      'the onboarding for the location "{organizationName}" has been submitted and is awaiting your review. Open immoOS using the button below to review the details.',
    'cta-label': 'Review onboarding',
    footnote: 'You are receiving this notification because you are an Owner or Workspace-Admin of this workspace.',
  },
  'onboarding-change-request': {
    subject: 'Changes required: onboarding for "{workspaceName}"',
    title: 'Please revise your details',
    greeting: 'Hi {fullName},',
    'greeting-fallback': 'Hi,',
    body:
      '{greeting}\n' +
      '\n' +
      'your onboarding details for "{workspaceName}" have been reviewed and need some changes. Reviewer notes:\n' +
      '\n' +
      '{reviewNotes}\n' +
      '\n' +
      'Open your onboarding using the button below to revise your details and resubmit.',
    'cta-label': 'Revise onboarding',
    footnote: 'If you weren’t expecting this message, you can safely ignore this email.',
  },
  'partner-onboarding-approved': {
    subject: 'Your location "{organisationName}" has been approved',
    title: 'Location approved',
    greeting: 'Hi {fullName},',
    'greeting-fallback': 'Hi,',
    body:
      '{greeting}\n' +
      '\n' +
      'your onboarding details for "{organisationName}" have been reviewed and approved. Your location is now active. Sign in using the button below to get started.',
    'cta-label': 'Sign in',
    footnote: 'If you weren’t expecting this message, you can safely ignore this email.',
  },
  'admin-invite-signup': {
    subject: 'You have been added as an immoOS platform admin',
    title: 'Welcome to the immoOS admin console',
    greeting: 'Hi {fullName},',
    'greeting-fallback': 'Hi,',
    body:
      '{greeting}\n' +
      '\n' +
      'you have been added as a platform admin on immoOS. Set up your account and use the button below to access the admin console.',
    'cta-label': 'Set up account',
    footnote: 'If you weren’t expecting this invitation, you can safely ignore this email.',
  },
  'admin-invite-signin': {
    subject: 'You have been added as an immoOS platform admin',
    title: 'Welcome to the immoOS admin console',
    greeting: 'Hi {fullName},',
    'greeting-fallback': 'Hi,',
    body:
      '{greeting}\n' + '\n' + 'you have been added as a platform admin on immoOS. Sign in to access the admin console.',
    'cta-label': 'Open admin console',
    footnote: 'If you weren’t expecting this invitation, you can safely ignore this email.',
  },
  'signing-invitation': {
    subject:
      '{companyName}: {documentType, select, BROKER_CONTRACT {Your brokerage agreement} other {Your power of attorney}} for signature',
    sent: '{agentName} from {companyName} has sent you {documentType, select, BROKER_CONTRACT {the brokerage agreement} other {the power of attorney}} for {propertyAddress} for signature. You will find the document as a PDF attached.',
    'sign-intro': 'You can sign it online without printing it:',
    'link-label':
      'View and sign the {documentType, select, BROKER_CONTRACT {brokerage agreement} other {power of attorney}}',
    personal: 'The link is meant for you only. Please do not forward this e-mail.',
    questions: 'If you have any questions, you can reach {agentName} on {phone} or by replying to this e-mail.',
    closing: 'Kind regards',
  },
  'signing-reminder': {
    subject:
      'Reminder: {documentType, select, BROKER_CONTRACT {your brokerage agreement is waiting} other {your power of attorney is waiting}} for your signature',
    pending:
      '{documentType, select, BROKER_CONTRACT {The brokerage agreement} other {The power of attorney}} for {propertyAddress} from {companyName} has not been signed yet. The document is attached as a PDF.',
  },
  'signing-acceptance-reminder': {
    subject: 'Please confirm your brokerage agreement by e-mail',
    intro:
      'thank you for signing the brokerage agreement for {propertyAddress}. Please confirm the conclusion briefly by replying to this e-mail with the following text:',
    'reply-link': 'Prepare a reply with this text: {url}',
    closing: 'Kind regards',
  },
  'signing-confirmation': {
    subject:
      'Confirmation: you have signed {documentType, select, BROKER_CONTRACT {the brokerage agreement} other {the power of attorney}}',
    signed:
      'You signed {documentType, select, BROKER_CONTRACT {the brokerage agreement} other {the power of attorney}} for {propertyAddress} on {date} at {time}. The document is attached as a PDF.',
    belehrung: 'The cancellation notice and the model cancellation form are part of the attached PDF.',
    'early-start-declared':
      'You expressly requested that we begin the brokerage work before the cancellation period has expired, and confirmed that you are aware that your right of withdrawal lapses once we have fully performed the contract. Your declaration read, word for word: “{declaration}” (made on {date} at {time}).',
    'no-early-start': 'We will only begin the brokerage work after the cancellation period has expired.',
    'acceptance-intro': 'Please also confirm the conclusion by replying to this e-mail with the following text:',
    'acceptance-reply-link': 'Prepare a reply with this text: {url}',
    'acceptance-reply-subject': 'Acceptance of brokerage agreement {propertyAddress}',
    'completion-follows': 'Once all owners have signed, you will receive the fully signed document.',
    withdrawal:
      'You can withdraw from the contract until the cancellation period expires, for example by e-mail to {email} or online using the “Withdraw from contract” button: {link} If you use the online function, you will immediately receive an acknowledgement with the content of your declaration and the date and time of receipt.',
    'withdrawal-online-only':
      'You can withdraw from the contract until the cancellation period expires, for example online using the “Withdraw from contract” button: {link} If you use the online function, you will immediately receive an acknowledgement with the content of your declaration and the date and time of receipt.',
    closing: 'Kind regards',
  },
  'signing-completion': {
    subject: '{documentType, select, BROKER_CONTRACT {Brokerage contract} other {Power of attorney}} fully signed',
    body: 'all parties have signed the {documentType, select, BROKER_CONTRACT {brokerage contract} other {power of attorney}} for {propertyAddress}. The fully signed document is attached as a PDF.',
  },
  'signing-completed': {
    subject: '{documentType, select, BROKER_CONTRACT {Brokerage contract} other {Power of attorney}} fully signed',
    greeting: 'Hi {firstName},',
    'greeting-fallback': 'Hi,',
    body: 'all parties have signed the {documentType, select, BROKER_CONTRACT {brokerage contract} other {power of attorney}} for {propertyAddress}. The fully signed document is attached as a PDF.',
    'cta-label': 'Go to deal',
  },
  'signing-signer-signed': {
    subject: '{name} has signed',
    greeting: 'Hi {firstName},',
    'greeting-fallback': 'Hi,',
    body: '{name} has signed {documentType, select, BROKER_CONTRACT {the brokerage agreement} other {the power of attorney}} for {propertyAddress}. {signed} of {total} owners have signed.',
    'cta-label': 'Go to deal',
  },
  'signing-revocation-received': {
    subject: 'Withdrawal: {name} has withdrawn from the brokerage agreement',
    greeting: 'Hi {firstName},',
    'greeting-fallback': 'Hi,',
    body: "{name} withdrew from the brokerage agreement for {propertyAddress} on {date} at {time}. The deal's phase and status stay unchanged. Check how the mandate should proceed.",
    'cta-label': 'Go to deal',
  },
  'brokerage-notice': {
    subject: 'Your brokerage agreement for {propertyTitle}',
    greeting: {
      mr: 'Dear Mr {lastName},',
      mrs: 'Dear Ms {lastName},',
      person: 'Dear {firstName} {lastName},',
      company: 'Dear Sir or Madam,',
    },
    interest: 'thank you for your interest in “{propertyTitle}” in {propertyCity}.',
    commission:
      'Before we may give you the address and the full property brochure, you conclude a brokerage agreement with us. The following commission applies to the buyer: {commission}',
    'link-intro': 'You can read the agreement and conclude it online at this link:',
    'notice-intro':
      'Please read the following cancellation notice first. It is also available on the page behind the link.',
    questions: 'If you have any questions, simply reply to this e-mail.',
    closing: 'Kind regards',
  },
  'brokerage-reminder': {
    subject: 'Reminder: your brokerage agreement for {propertyTitle}',
    reminder:
      'a few days ago we sent you the brokerage agreement for “{propertyTitle}” in {propertyCity}. Once you have concluded it, we can give you the address and the full property brochure.',
    'link-intro': 'You can read the agreement and conclude it online here:',
    'no-interest': 'If you are no longer interested, you do not need to do anything.',
  },
  'brokerage-confirmation': {
    subject: 'Confirmation of your brokerage agreement for {propertyTitle}',
    concluded: 'On {date} at {time} you concluded the following brokerage agreement with {companyName}.',
    declarations: 'Your declarations:',
    'declaration-read': '- Read the cancellation notice and the model cancellation form: yes',
    'declaration-early-start': '- Requested early start and acknowledged the loss of the right of withdrawal: {answer}',
    'declaration-verbatim': 'Your declaration read verbatim: "{declaration}" (made on {date} at {time}).',
    'acceptance-reply-intro': 'Please also confirm the conclusion by replying to this e-mail with the following text:',
    'acceptance-reply-address': 'Your reply goes to {address}.',
    'acceptance-reply-link': 'Prepare a reply with this text: {url}',
    'acceptance-reply-subject': 'Acceptance of brokerage agreement {contractNumber}',
    yes: 'yes',
    no: 'no',
    'expose-download': 'You can download the full exposé here: {url}',
    'expose-soon': 'We will send you the full exposé shortly.',
    'expose-not-requested': 'As you did not request an early start, we will get in touch with you.',
    'link-intro': 'You can view your contract at any time here, and withdraw from it there as well:',
    closing: 'Kind regards',
  },
  'brokerage-acceptance-reminder': {
    subject: 'Please confirm your brokerage agreement by e-mail',
    intro:
      'thank you for concluding the brokerage agreement for “{propertyTitle}”. Please briefly confirm the conclusion by replying to this e-mail with the following text:',
    closing: 'Kind regards',
  },
  'brokerage-agreement-concluded': {
    subject: 'Brokerage agreement concluded: {buyerName} — {propertyTitle}',
    title: 'Brokerage agreement concluded',
    greeting: 'Hello {firstName},',
    'greeting-fallback': 'Hello,',
    body: '{buyerName} concluded the brokerage agreement for “{propertyTitle}” online on {date} at {time}.',
    'early-start-expose':
      'An early start was requested — you can start work straight away. The exposé was offered for download.',
    'early-start-no-expose':
      'An early start was requested — you can start work straight away. The property has no full exposé yet; please send the exposé yourself.',
    'no-early-start':
      'No early start was requested. The withdrawal period ends on {date}; until then, please do not provide any services such as the exposé or viewings.',
    'cta-label': 'Go to prospect',
  },
  'data-room-release': {
    subject: 'Documents for {propertyTitle}',
    released:
      'we have released the documents for “{propertyTitle}” in {propertyCity} to you. You will find them on your personal page, where you can also view your brokerage agreement:',
    'released-no-city':
      'we have released the documents for “{propertyTitle}” to you. You will find them on your personal page, where you can also view your brokerage agreement:',
    'released-without-contract':
      'we have released the documents for “{propertyTitle}” in {propertyCity} to you. You will find them on your personal page:',
    'released-without-contract-no-city':
      'we have released the documents for “{propertyTitle}” to you. You will find them on your personal page:',
    'how-to': 'There you can view and download every document. We will also make new documents available to you there.',
  },
  'brokerage-withdrawal-receipt': {
    subject: 'Acknowledgement of receipt of your withdrawal',
    received: 'Your withdrawal reached us on {date} at {time}. You declared the following:',
    name: 'Name: {value}',
    contract: 'Contract: {value}',
    email: 'E-mail address: {value}',
    reason: 'Reason: {value}',
    'reason-none': '—',
    closing: 'Kind regards',
  },
  'brokerage-withdrawal-received': {
    subject: 'Withdrawal: {buyerName} — {propertyTitle}',
    title: 'Brokerage agreement withdrawn',
    greeting: 'Hello {firstName},',
    'greeting-fallback': 'Hello,',
    body: '{buyerName} withdrew from the brokerage agreement for “{propertyTitle}” on {date} at {time}.',
    reason: 'Reason: {reason}',
    'reason-none': 'none given',
    'moved-to-lost':
      'The prospect is now “Lost” with the reason “Withdrawal exercised”. Please stop working for this prospect.',
    'unchanged-lost':
      'The purchase process was already lost and stays unchanged. Please check what the withdrawal means for it.',
    'unchanged-won':
      'The purchase process was already won and stays unchanged. Please check what the withdrawal means for it.',
    'cta-label': 'Go to prospect',
  },
  'new-inquiry': {
    subject: 'New inquiry: {name} for {propertyTitle} ({location})',
    'subject-no-location': 'New inquiry: {name} for {propertyTitle}',
    title: 'New inquiry',
    greeting: 'Hi {firstName},',
    'greeting-fallback': 'Hi,',
    intro:
      'a new inquiry about your property came in via {source}. We have added {name} as a prospect in the sales pipeline.',
    'fallback-note': 'You are receiving this inquiry because the property has no active person in charge.',
    'group-prospect': 'PROSPECT',
    'line-name': 'Name: {name}',
    'line-email': 'Email: {email}',
    'line-phone': 'Phone: {phone}',
    'group-message': 'MESSAGE',
    'group-property': 'PROPERTY',
    'cta-label': 'Open prospect',
    closing: 'Best regards',
    signature: 'your immoOS team',
    'source-label':
      '{source, select, WEBSITE {your website} WEB_EXPOSE {your web exposé} IMMOSCOUT24 {ImmoScout24} other {a source}}',
  },
  agency: {
    imprint: {
      phone: 'Phone: {value}',
      email: 'Email: {value}',
      'managing-directors': 'Managing directors: {value}',
      register: 'Register court: {court} · Register number: {number}',
      'register-court-only': 'Register court: {court}',
      'register-number-only': 'Register number: {number}',
      'vat-id': 'VAT ID: {value}',
      'supervisory-authority': 'Supervisory authority: {value}',
      'trade-permit-authority': 'Licensing authority (§ 34c GewO): {value}',
      'professional-indemnity-insurance': 'Professional indemnity insurance: {value}',
    },
    'privacy-line': 'Privacy: {url}',
  },
  'viewing-invitation': {
    subject: 'Viewing invitation: {propertyTitle} on {weekday}, {date}',
    heading: 'Your viewing on {weekday}, {date}',
    'greeting-mr': 'Dear Mr {lastName},',
    'greeting-mrs': 'Dear Ms {lastName},',
    'greeting-name': 'Dear {firstName} {lastName},',
    'greeting-neutral': 'Hello,',
    intro: 'we look forward to showing you "{propertyTitle}" in person. Here are the details of your appointment:',
    duration: '{minutes} minutes',
    'when-all-day': '{weekday}, {date} · all day',
    route: 'Plan route',
    'hint-label': 'Note from {firstName}:',
    'hint-label-anonymous': 'Note:',
    closing:
      'You will find the appointment attached — one click adds it to your calendar, and you can accept straight away. If the time does not suit you, simply reply to this e-mail and we will find another one together.',
    'sign-off': 'We look forward to seeing you.',
    'calendar-summary': 'Viewing {subject}',
    'when-short': '{weekday}, {date} · {time}',
    rescheduled: {
      subject: 'New date for your viewing: {propertyTitle}',
      heading: 'Your viewing has been rescheduled',
      intro: 'Your viewing of "{propertyTitle}" will take place at a new time:',
      'new-label': 'New',
      'previous-label': 'Previously:',
      closing:
        'The attachment updates the entry in your calendar. If the new time does not suit you, simply reply to this e-mail.',
    },
    cancelled: {
      subject: 'Cancelled: your viewing of {propertyTitle} on {date}',
      heading: 'Your viewing has been cancelled',
      intro:
        'unfortunately we have to cancel your viewing of "{propertyTitle}" on {weekday}, {date} at {time}. The attachment removes the entry from your calendar.',
      'intro-all-day':
        'unfortunately we have to cancel your viewing of "{propertyTitle}" on {weekday}, {date}. The attachment removes the entry from your calendar.',
      label: 'Cancelled',
      closing: 'Would you like a new appointment? Simply reply to this e-mail or give us a call.',
    },
    reminder: {
      subject: 'Reminder: your viewing tomorrow at {time} — {propertyTitle}',
      'subject-all-day': 'Reminder: your viewing tomorrow — {propertyTitle}',
      heading: 'Your viewing is tomorrow',
      intro:
        'we look forward to seeing you tomorrow! Here is everything you need for your viewing of "{propertyTitle}":',
      closing: 'Something come up? Simply reply to this e-mail or give us a call.',
      'sign-off': 'See you tomorrow!',
    },
  },
  buyer: {
    'contact-heading': 'Your contact',
    call: 'Call',
    privacy: 'Privacy policy: {url}',
    'managing-directors': 'Management: {value}',
    register: 'Register court: {court}, {number}',
    'register-court-only': 'Register court: {court}',
    'register-number-only': 'Register number: {number}',
    'vat-id': 'VAT ID: {value}',
    'supervisory-authority': 'Supervisory authority: {value}',
    'trade-permit-authority': 'Licensing authority under § 34c GewO: {value}',
    'phone-email': 'Phone {phone} · Email {email}',
    'phone-only': 'Phone {phone}',
    'email-only': 'Email {email}',
  },
  'viewing-declined': {
    subject: 'Viewing declined: {name} — {propertyTitle}',
    greeting: 'Hi {firstName},',
    'greeting-fallback': 'Hi,',
    body: '{name} declined the viewing of "{propertyTitle}" on {weekday}, {date} at {time}.',
    'body-all-day': '{name} declined the viewing of "{propertyTitle}" on {weekday}, {date}.',
    'next-step': 'The appointment stays in place. Cancel it or arrange a new one.',
    link: 'Go to the prospect: {url}',
    closing: 'Best regards',
    signature: 'Your immoOS team',
  },
};
